import React, { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Redirect, Route, Switch, useRouteMatch } from "react-router";
import { KButton } from "@kyburz/kdesign-system";

import { VehicleTemplateColumn } from "../typings/VehicleTemplateColumn";
import { useGetVehicleTemplates } from "../requests/GetVehicleTemplates";
import { useUpdateVehicleTemplates } from "../requests/UpdateVehicleTemplate";
import useSetState from "../../../hooks/useTypedSetState";

import BaseLayout from "../../../components/layouts/BaseLayout";
import EditingTab from "../components/tabs/EditingTab";
import CompaniesTab from "../components/tabs/CompaniesTab";
import {
  ChangableVehicleTemplate,
  VehicleTemplate,
} from "../facades/VehicleTemplate";
import { OdometerSource } from "../typings/RawVehicleTemplate";
import { useStyles } from "./styles";

const VehicleTemplateEditing: React.FC = () => {
  const classes = useStyles();

  const history = useHistory();

  const match = useRouteMatch<{ templateId?: string; tab?: string }>({
    path: "/config/vehicle/templates/templates/:templateId/:tab",
    strict: true,
    sensitive: true,
  });

  const { templateId, tab } = match?.params || {};

  const [{ activeTab }, setState] = useSetState({ activeTab: tab });

  useEffect(() => {
    setState({ activeTab: tab });
  }, [setState, tab]);

  const handleTabClick = (_, newValue) => {
    history.push(
      `/config/vehicle/templates/templates/${templateId}/${newValue}`,
    );
  };

  const { loading: saveLoading, request: updateTemplate } =
    useUpdateVehicleTemplates();

  const [template, setTemplate] = useSetState<ChangableVehicleTemplate>({
    name: "",
    description: "",
    isDefaultTemplate: false,
    ignitionValue: "",
    columns: [] as VehicleTemplateColumn[],
    tags: [] as string[],
    companies: [] as string[],
    owner: "",
    displayColumns: null as string[] | null,
    errorCodesMappingName: null as string | null,
    internalTariffId: null as string | null,
    odometerSource: OdometerSource.Can,
  });

  const { data = [] } = useGetVehicleTemplates(
    useMemo(
      () => ({
        filters: [{ id: "_id", value: templateId || "", type: "eq" }],
      }),
      [templateId],
    ),
  );
  const templatedForEditing: VehicleTemplate | undefined = data[0];

  useEffect(() => {
    if (templatedForEditing) {
      setTemplate({
        name: templatedForEditing.name,
        description: templatedForEditing.description,
        isDefaultTemplate: templatedForEditing.isDefaultTemplate,
        ignitionValue: templatedForEditing.ignitionValue,
        columns: templatedForEditing.columns,
        tags: templatedForEditing.tags,
        companies: templatedForEditing.companies,
        owner: templatedForEditing.owner,
        displayColumns: templatedForEditing.displayColumns,
        errorCodesMappingName: templatedForEditing.errorCodesMappingName,
        internalTariffId: templatedForEditing.internalTariffId,
        odometerSource: templatedForEditing.odometerSource,
      });
    }
  }, [templatedForEditing, setTemplate]);

  const goBack = () => {
    history.push("/config/vehicle/templates/templates/");
  };

  const onSave = async () => {
    await updateTemplate({ template: templatedForEditing, updated: template });
    goBack();
  };

  return (
    <BaseLayout
      title="Edit Vehicle Template"
      handleArrowClick={goBack}
      activeTab={activeTab}
      tabs={[
        { label: "Template Editor", value: "editor" },
        { label: "Companies", value: "companies" },
      ]}
      handleTabChange={handleTabClick}
      mainClassname={classes.layout}
    >
      <div className={classes.main}>
        <Switch>
          <Route
            path="/config/vehicle/templates/templates/:templateId/editor"
            exact
          >
            <EditingTab template={template} onChangeTemplate={setTemplate} />
          </Route>
          <Route path="/config/vehicle/templates/templates/:templateId/companies">
            <CompaniesTab
              title={`Vehicle template: ${template.name}`}
              selectedCompanies={template.companies}
              onCompaniesChange={(companies) => setTemplate({ companies })}
            />
          </Route>
          <Redirect to="/config/vehicle/templates/templates/:templateId/editor" />
        </Switch>
      </div>

      <footer className={classes.buttons}>
        <KButton children="Cancel" theme="secondary" onClick={goBack} />

        <KButton
          children="Save and close"
          onClick={onSave}
          loading={saveLoading}
        />
      </footer>
    </BaseLayout>
  );
};

export default VehicleTemplateEditing;
